/**
 * Player cast bar for the skill cluster: thin bar with the spell icon, name, time left and an
 * "Esc" cancel hint (Esc is handled by the input layer: session `escape` → player.escape).
 *
 * Shows the sim's cast or the local predicted one (game/prediction.ts shownCast), so it appears
 * the same frame as the key press. The fill is a scaleX transform from the interned SCALE_X table
 * on the sub-tick clock (hudNow); the time label is keyed by tenths and only rewritten on change.
 *
 * +castux:
 *  - the spell-queue window: the last SPELL_QUEUE_WINDOW_MS of the bar is shaded (right end of a
 *    cast, left end of a draining channel) and lights up once the bar is inside it (a press
 *    now queues);
 *  - outcomes (castOutcome.ts): a brief bright flash on success; red "Interrupted" with the
 *    locked-out school and its countdown; grey "Cancelled" when moved / cancelled.
 *
 * Probe contract: the root carries `player-cast` and toggles `active` while a cast is shown.
 */
import type { ContentRegistry, DeepReadonly, SimEvent, Unit, UnitId, World } from '@keystone/sim';
import { hudNow, shownCast } from '../../game/prediction';
import { inQueueWindow, queueFraction } from '../../game/queueWindow';
import { el, setText } from '../dom';
import { iconUrl } from '../icons';
import { TENTHS, SCALE_X, barIndex } from '../kit/tokens';
import { castTooltipModel } from '../tooltip/hud';
import { attachAbilityTooltip } from '../tooltip/view';
import { castTenths } from './derive';
import {
  CANCELLED_SHOW_MS,
  type CastOutcomeKind,
  type CastOutcomeNote,
  INTERRUPTED_MIN_MS,
  LOCKOUT_SHOW_MAX_MS,
  SUCCESS_FLASH_MS,
  endedOutcome,
  outcomeFromEvent,
  outcomeLabel,
} from './castOutcome';

const OUTCOME_CLS: Readonly<Record<CastOutcomeKind, string>> = { success: 'done', interrupted: 'interrupted', cancelled: 'cancelled' };

export class PlayerCastBar {
  readonly root: HTMLElement;
  private readonly fill: HTMLElement;
  private readonly queue: HTMLElement;
  private readonly icon: HTMLImageElement;
  private readonly name: HTMLElement;
  private readonly time: HTMLElement;
  private active = false;
  private kind: 'cast' | 'channel' | '' = '';
  private locked = false;
  private abilityId: string | null = null;
  private fillIdx = -1;
  private queueIdx = -1;
  private inQueue = false;
  private tenths = -1;
  /** The cast shown last frame (to judge how it ended). */
  private lastEndsAt = 0;
  private hadCast = false;
  /** Outcome announced by this frame's events, consumed when the bar ends. */
  private pending: CastOutcomeNote | null = null;
  private outcome: CastOutcomeKind | null = null;
  private outcomeUntil = 0;
  /** Interrupted by a kick: the locked school (countdown from the unit's lockout). */
  private lockSchool: string | null = null;
  private holding = false;
  private tip: { world: DeepReadonly<World>; content: ContentRegistry; unitId: UnitId | null } | null = null;

  constructor(parent: HTMLElement) {
    this.root = el('div', 'hkc-cast player-cast', parent);
    const track = el('div', 'hkc-cast-track', this.root);
    this.queue = el('div', 'hkc-cast-queue', track);
    this.fill = el('div', 'hkc-cast-fill', track);
    el('div', 'hkc-cast-spark', this.fill);
    this.icon = el('img', 'hkc-cast-icon', this.root);
    this.icon.alt = '';
    this.icon.draggable = false;
    this.icon.decoding = 'async';
    this.name = el('div', 'hkc-cast-name', this.root);
    this.time = el('div', 'hkc-cast-time hk-num', this.root);
    const esc = el('div', 'hkc-cast-esc', this.root);
    el('span', 'hk-keycap', esc, 'Esc');
    attachAbilityTooltip(this.root, () => (this.tip && this.active ? castTooltipModel({ world: this.tip.world, content: this.tip.content, playerId: null }, this.tip.unitId) : null));
  }

  /** Interrupt / cancel of the player's cast → the outcome shown when the bar ends. */
  ingest(events: readonly SimEvent[], playerId: UnitId | null, _realNow: number): void {
    if (!playerId) return;
    for (const e of events) {
      const o = outcomeFromEvent(e, playerId);
      // A kick outranks a cancel from the same batch.
      if (o && !(this.pending?.kind === 'interrupted' && o.kind === 'cancelled')) this.pending = o;
    }
  }

  update(u: DeepReadonly<Unit> | undefined, world: DeepReadonly<World>, content: ContentRegistry, realNow: number): void {
    if (this.tip) {
      this.tip.world = world;
      this.tip.content = content;
      this.tip.unitId = u?.id ?? null;
    } else this.tip = { world, content, unitId: u?.id ?? null };
    const c = u?.alive ? shownCast(world, u.id) : null;
    const now = hudNow(world);
    const on = !!c;
    if (on !== this.active) {
      this.active = on;
      this.root.classList.toggle('active', on);
    }
    if (c) {
      // A new cast replaces any outcome still on the bar.
      if (this.outcome) this.setOutcome(null, realNow, world, content, u);
      this.pending = null;
      this.hadCast = true;
      this.lastEndsAt = c.endsAt;
    } else {
      if (this.hadCast) {
        this.hadCast = false;
        const k = u?.alive ? endedOutcome(this.pending, this.lastEndsAt, now) : null;
        if (k) this.setOutcome(k, realNow, world, content, u);
      } else if (this.pending && u?.alive) {
        // An interrupt / cancel with no bar shown (e.g. a predicted bar already gone): still say so.
        this.setOutcome(this.pending.kind, realNow, world, content, u);
      }
      this.pending = null;
      this.drawOutcome(realNow, world, u);
      return;
    }
    if (c.abilityId !== this.abilityId) {
      this.abilityId = c.abilityId;
      const a = content.ability(c.abilityId);
      setText(this.name, a?.name ?? c.abilityId);
      const url = iconUrl(c.abilityId, 64);
      if (url) {
        if (this.icon.getAttribute('src') !== url) this.icon.src = url;
        this.icon.hidden = false;
      } else this.icon.hidden = true;
    }
    if (c.kind !== this.kind) {
      this.root.classList.toggle('channel', c.kind === 'channel');
      this.kind = c.kind;
    }
    const locked = !c.interruptible;
    if (locked !== this.locked) {
      this.locked = locked;
      this.root.classList.toggle('locked', locked);
    }
    // Sub-tick clock: the bar moves every frame instead of in 50 ms steps. Channels drain, casts fill.
    const total = Math.max(1, c.endsAt - c.startedAt);
    const done = Math.min(1, Math.max(0, (now - c.startedAt) / total));
    const fi = barIndex(c.kind === 'channel' ? 1 - done : done);
    if (fi !== this.fillIdx) {
      this.fillIdx = fi;
      this.fill.style.transform = SCALE_X[fi]!;
    }
    // +castux: queue window (anchored at the bar's end: right for casts, left for channels via CSS).
    const qi = barIndex(queueFraction(total));
    if (qi !== this.queueIdx) {
      this.queueIdx = qi;
      this.queue.style.transform = SCALE_X[qi]!;
    }
    const inQ = inQueueWindow(c.endsAt - now);
    if (inQ !== this.inQueue) {
      this.inQueue = inQ;
      this.root.classList.toggle('in-queue', inQ);
    }
    const t = castTenths(c.endsAt - now);
    if (t !== this.tenths) {
      this.tenths = t;
      this.time.textContent = t === 0 ? '0.0' : t < 100 ? TENTHS[t]! : (t / 10).toFixed(1);
    }
  }

  private setOutcome(k: CastOutcomeKind | null, realNow: number, world: DeepReadonly<World>, content: ContentRegistry, u: DeepReadonly<Unit> | undefined): void {
    if (this.outcome) this.root.classList.remove(OUTCOME_CLS[this.outcome], 'hold');
    this.outcome = k;
    this.holding = false;
    this.lockSchool = null;
    if (this.inQueue) {
      this.inQueue = false;
      this.root.classList.remove('in-queue');
    }
    if (!k) return;
    let ms = k === 'success' ? SUCCESS_FLASH_MS : k === 'cancelled' ? CANCELLED_SHOW_MS : INTERRUPTED_MIN_MS;
    if (k === 'interrupted' && this.pending?.kickedAbilityId) {
      const school = content.ability(this.pending.kickedAbilityId)?.school ?? null;
      const lock = school && u ? u.lockouts[school] : undefined;
      if (school && lock !== undefined && lock > world.time) {
        this.lockSchool = school;
        ms = Math.max(ms, Math.min(LOCKOUT_SHOW_MAX_MS, lock - world.time) + 250);
        this.holding = true;
        this.root.classList.add('hold');
      }
    }
    this.outcomeUntil = realNow + ms;
    this.root.classList.add(OUTCOME_CLS[k]);
    if (k !== 'success') setText(this.name, outcomeLabel(k, this.lockSchool));
    this.time.textContent = '';
    this.fillIdx = -1;
    this.fill.style.transform = SCALE_X[SCALE_X.length - 1]!;
    // Force the next cast to rewrite its name and time.
    this.abilityId = null;
    this.tenths = -1;
  }

  /** Outcome display: lockout countdown, then the fade (CSS), then off. */
  private drawOutcome(realNow: number, world: DeepReadonly<World>, u: DeepReadonly<Unit> | undefined): void {
    if (!this.outcome) return;
    if (realNow >= this.outcomeUntil) {
      this.root.classList.remove(OUTCOME_CLS[this.outcome], 'hold');
      this.outcome = null;
      this.holding = false;
      return;
    }
    if (this.holding) {
      const lock = this.lockSchool && u ? u.lockouts[this.lockSchool as keyof typeof u.lockouts] : undefined;
      const left = lock !== undefined ? lock - hudNow(world) : 0;
      if (left <= 0) {
        // Lockout over: let the red bar fade out now.
        this.holding = false;
        this.root.classList.remove('hold');
        this.outcomeUntil = realNow + 900; // the CSS fade (hkc-cast-fade 0.9 s)
        this.time.textContent = '';
        this.tenths = -1;
        return;
      }
      const t = castTenths(left);
      if (t !== this.tenths) {
        this.tenths = t;
        this.time.textContent = t < 100 ? TENTHS[t]! : (t / 10).toFixed(1);
      }
    }
  }
}
